import type { ThemeId } from "@/hooks/useTheme";

// ── Theme configuration ────────────────────────────────────────────────────────

const THEME_EMOJIS: Record<ThemeId, string[]> = {
  acorns:   ["🐿️", "🌰", "🍂", "🍁", "🌿"],
  campfire: ["🔥", "✨", "🌟", "💫", "🪵"],
  forest:   ["🌲", "🍄", "🌿", "🦌", "🌱"],
  night:    ["🌙", "⭐", "🦉", "✨", "💫"],
  garden:   ["🌸", "🌿", "🦋", "🌺", "🍀"],
  magical:  ["🦄", "🌈", "💫", "✨", "🪄"],
  ocean:    ["🌊", "🐚", "⛵", "🐠", "💙"],
  stars:    ["⭐", "🌙", "✨", "💫", "🌟"],
};

// ── Deterministic element generation ─────────────────────────────────────────
// Using a seeded sequence so positions are stable across re-renders.

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

interface DecorElement {
  emoji: string;
  top: string;
  left: string;
  size: string;
  opacity: number;
  duration: string;
  delay: string;
}

function buildElements(theme: ThemeId): DecorElement[] {
  const emojis = THEME_EMOJIS[theme];
  const rand = seededRandom(emojis.join("").charCodeAt(0) * 31);
  const COUNT = 20;
  const elements: DecorElement[] = [];

  for (let i = 0; i < COUNT; i++) {
    // Distribute vertically in bands so elements spread across the full height
    const band = i / COUNT;
    const top = band * 90 + rand() * 9; // 0–99%

    // Alternate left/right halves with some randomness
    const left = i % 2 === 0
      ? rand() * 18                 // left edge zone  0–18%
      : 78 + rand() * 18;           // right edge zone 78–96%

    const size = 16 + Math.floor(rand() * 17); // 16–32px
    const opacity = 0.12 + rand() * 0.06;       // 0.12–0.18
    const duration = 8 + Math.floor(rand() * 9); // 8–16s
    const delay = Math.floor(rand() * 9);         // 0–8s

    elements.push({
      emoji: emojis[i % emojis.length],
      top: `${top.toFixed(1)}%`,
      left: `${left.toFixed(1)}%`,
      size: `${size}px`,
      opacity: parseFloat(opacity.toFixed(3)),
      duration: `${duration}s`,
      delay: `${delay}s`,
    });
  }

  return elements;
}

// Pre-compute once per theme (module-level, no re-render cost)
const ELEMENTS: Record<ThemeId, DecorElement[]> = {
  acorns:   buildElements("acorns"),
  campfire: buildElements("campfire"),
  forest:   buildElements("forest"),
  night:    buildElements("night"),
  garden:   buildElements("garden"),
  magical:  buildElements("magical"),
  ocean:    buildElements("ocean"),
  stars:    buildElements("stars"),
};

// ── Component ─────────────────────────────────────────────────────────────────

interface BackgroundDecorProps {
  theme: ThemeId;
}

const BackgroundDecor = ({ theme }: BackgroundDecorProps) => {
  const elements = ELEMENTS[theme] ?? ELEMENTS.campfire;

  return (
    <>
      <style>{`
        @keyframes bgFloat {
          0%   { transform: translateY(0px)   rotate(0deg);  }
          33%  { transform: translateY(-20px) rotate(5deg);  }
          66%  { transform: translateY(-10px) rotate(-5deg); }
          100% { transform: translateY(0px)   rotate(0deg);  }
        }
      `}</style>

      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        {elements.map((el, i) => (
          <span
            key={`${theme}-${i}`}
            style={{
              position: "absolute",
              top: el.top,
              left: el.left,
              fontSize: el.size,
              opacity: el.opacity,
              lineHeight: 1,
              animation: `bgFloat ${el.duration} ease-in-out ${el.delay} infinite`,
            }}
          >
            {el.emoji}
          </span>
        ))}
      </div>
    </>
  );
};

export default BackgroundDecor;
